/**
 * 形状工具（补齐 registry.ts 早先登记的占位工具）。
 *
 * 矩形/椭圆/直线三种变体共用一个工具 id（`kind` 选项区分，跟选框的
 * `shape` 选项同一个套路），工具栏用长按分组切出三个入口
 * （registry.ts 的 `TOOL_GROUPS`）。交互是标准的按下定起点、拖拽定终点、
 * 松手提交：矩形/椭圆按起止点的外接矩形建路径，直线按起止点连线
 * 建一条宽度可调的细长矩形（真正落盘在 app/shape-create.ts）。
 */
import { createShapeFromDrag, type ShapeKind } from '../../app/shape-create';
import { lineAnchors } from '../../engine/path/anchor';
import type { AppStore } from '../../app/store';
import type { VectorFill, VectorStroke } from '../../document/types';
import type { ToolManager } from './registry';

function hexToRgb(hex: string): readonly [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

/**
 * 矢量取色转成 canvas 的 fillStyle/strokeStyle——渐变按传入 bounds 从左到右，
 * 跟 app/composite.ts 的 sampleVectorFill 用同一套几何约定，拖拽预览跟
 * 提交后的真实渲染看起来才是一回事，不是"预览一个样、落盘另一个样"。
 */
function vectorFillToCanvasStyle(
  ctx: CanvasRenderingContext2D,
  fill: VectorFill,
  bounds: { x: number; y: number; width: number; height: number },
): string | CanvasGradient {
  if (fill.type === 'solid') {
    const [r, g, b] = hexToRgb(fill.color);
    return `rgba(${r}, ${g}, ${b}, ${fill.alpha ?? 1})`;
  }
  const gradient = ctx.createLinearGradient(
    bounds.x,
    bounds.y + bounds.height / 2,
    bounds.x + bounds.width,
    bounds.y + bounds.height / 2,
  );
  for (const stop of fill.stops) gradient.addColorStop(Math.min(1, Math.max(0, stop.offset)), stop.color);
  return gradient;
}

export interface ShapePoint {
  readonly x: number;
  readonly y: number;
}

export interface ShapeToolDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
  /** 属性栏当前的填充取色（纯色/渐变），落盘那一刻才读一次。 */
  readonly fill: () => VectorFill;
  /** 属性栏当前的描边取色+宽度；宽度为 0 视为不描边。 */
  readonly stroke: () => VectorStroke | undefined;
}

export class ShapeToolController {
  readonly #deps: ShapeToolDeps;
  #start: ShapePoint | null = null;
  #current: ShapePoint | null = null;

  constructor(deps: ShapeToolDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#start !== null;
  }

  handlePointer(phase: 'down' | 'move' | 'up' | 'double', point: ShapePoint): boolean {
    const { store, tools } = this.#deps;
    if (tools.active.id !== 'shape') return false;
    if (store.document === null) return true;

    if (phase === 'down') {
      this.#start = point;
      this.#current = point;
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'move' && this.#start !== null) {
      this.#current = point;
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'up' && this.#start !== null) {
      const from = this.#start;
      const to = this.#current ?? point;
      this.#start = null;
      this.#current = null;
      const kind = tools.getOption('shape', 'kind') as ShapeKind;
      const lineWeight = tools.getOption('shape', 'lineWeight') as number;
      const radius = tools.getOption('shape', 'radius') as number;
      createShapeFromDrag(store, kind, from, to, lineWeight, this.#deps.fill(), this.#deps.stroke(), radius);
      this.#deps.requestRender();
      return true;
    }
    return true;
  }

  /**
   * 拖拽期间的实时预览：直接按属性栏当前的填充/描边取色画出形状本身
   * （矩形/椭圆/直线都填充+描边一起画），不是一个跟最终外观没关系的
   * 虚线框——拖的时候看不出会画成什么样，拖完才知道，体验上很别扭。
   */
  drawOverlay(ctx: CanvasRenderingContext2D): void {
    if (this.#start === null || this.#current === null) return;
    const kind = this.#deps.tools.getOption('shape', 'kind') as ShapeKind;
    const lineWeight = this.#deps.tools.getOption('shape', 'lineWeight') as number;
    const from = this.#start;
    const to = this.#current;
    const fill = this.#deps.fill();
    const stroke = this.#deps.stroke();

    ctx.save();
    ctx.beginPath();
    let bounds: { x: number; y: number; width: number; height: number };
    if (kind === 'ellipse') {
      const cx = (from.x + to.x) / 2;
      const cy = (from.y + to.y) / 2;
      const rx = Math.abs(to.x - from.x) / 2;
      const ry = Math.abs(to.y - from.y) / 2;
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      bounds = { x: cx - rx, y: cy - ry, width: rx * 2, height: ry * 2 };
    } else if (kind === 'line') {
      const points = lineAnchors(from, to, lineWeight).map((a) => a.point);
      ctx.moveTo(points[0]!.x, points[0]!.y);
      for (const p of points.slice(1)) ctx.lineTo(p.x, p.y);
      ctx.closePath();
      const xs = points.map((p) => p.x);
      const ys = points.map((p) => p.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      bounds = { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
    } else {
      const x0 = Math.min(from.x, to.x);
      const y0 = Math.min(from.y, to.y);
      const width = Math.abs(to.x - from.x);
      const height = Math.abs(to.y - from.y);
      const radius = this.#deps.tools.getOption('shape', 'radius') as number;
      if (radius > 0) {
        // 跟 roundedRectangleAnchors 同一个夹取规则：半径不能超过短边
        // 一半，预览和最终落盘的形状必须一致
        ctx.roundRect(x0, y0, width, height, Math.min(radius, width / 2, height / 2));
      } else {
        ctx.rect(x0, y0, width, height);
      }
      bounds = { x: x0, y: y0, width, height };
    }

    ctx.fillStyle = vectorFillToCanvasStyle(ctx, fill, bounds);
    ctx.fill();
    if (stroke !== undefined && stroke.width > 0) {
      ctx.lineWidth = stroke.width;
      ctx.strokeStyle = vectorFillToCanvasStyle(ctx, stroke.fill, bounds);
      ctx.stroke();
    }
    ctx.restore();
  }
}
